"use client";
import React, { useState, memo } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Avatar,
  Chip,
  IconButton,
  Modal,
  Button,
  Stack,
} from "@mui/material";
import NextLink from "next/link";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CloseIcon from "@mui/icons-material/Close";
import { styled } from "@mui/material/styles";
import Image from "next/image";
import "../app/globals.css";
import { useRouter } from "next/navigation";
import { useTranslation } from "react-i18next";
import { IMAGES } from "../constants/images";

// Styled components
const VideoCard = styled(Card)(() => ({
  position: "relative",
  borderRadius: "20px",
  overflow: "hidden",
  cursor: "pointer",
  boxShadow: "none",
  border: "none",
  backgroundColor: "transparent",
  height: "100%",
}));

const VideoThumbnail = styled(Box)(() => ({
  position: "relative",
  borderRadius: "20px",
  overflow: "hidden",
  height: "352px",
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundColor: "#a8a29e",
}));

const TextCard = styled(Box)(() => ({
  backgroundColor: "#F8F3FF",
  borderRadius: "20px",
  padding: "20px",
  border: "1px solid #F8F3FF",
  display: "flex",
  flexDirection: "column",
}));

const VideoDurationBadge = styled(Box)(() => ({
  position: "absolute",
  bottom: "12px",
  left: "12px",
  right: "12px",
  background: "#9048FB",
  color: "white",
  padding: "12px 20px",
  borderRadius: "12px",
  fontSize: "12px",
  fontWeight: "600",
  display: "flex",
  alignItems: "center",
  gap: "12px",
}));

// ✅ PERFORMANCE: Memoize this component to prevent unnecessary re-renders
const TestimonialsComponent = memo(() => {
  const router = useRouter();
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [selectedVideoUrl, setSelectedVideoUrl] = useState("");
  const [isContentLoaded, setIsContentLoaded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const videoRef = React.useRef(null);
  const progressBarRef = React.useRef(null);

  const handleVideoClick = (videoUrl) => {
    setSelectedVideoUrl(videoUrl);
    setIsVideoModalOpen(true);
    setIsPlaying(true);
    setProgress(0);
    setCurrentTime(0);
  };

  const togglePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleVideoEnd = () => {
    setIsPlaying(false);
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const current = videoRef.current.currentTime;
      const total = videoRef.current.duration;
      setCurrentTime(current);
      setDuration(total);
      setProgress((current / total) * 100);
    }
  };

  const handleSeek = (e) => {
    if (videoRef.current && progressBarRef.current) {
      const rect = progressBarRef.current.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const width = rect.width;
      const percentage = Math.max(0, Math.min(1, clickX / width));
      const newTime = percentage * videoRef.current.duration;
      videoRef.current.currentTime = newTime;
      setProgress(percentage * 100);
    }
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    handleSeek(e);
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      handleSeek(e);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging]);

  const formatTime = (seconds) => {
    if (isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // ✅ PERFORMANCE: Simulate content loading for skeleton screen demo
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setIsContentLoaded(true);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  // ✅ PERFORMANCE: Skeleton screen component
  const SkeletonCard = () => (
    <Box
      sx={{
        backgroundColor: "#F8F3FF",
        borderRadius: "20px",
        padding: "20px",
        border: "1px solid #F8F3FF",
        display: "flex",
        flexDirection: "column",
        background:
          "linear-gradient(90deg, #F8F3FF 25%, #E8E0FF 50%, #F8F3FF 75%)",
        backgroundSize: "200% 100%",
        animation: "skeleton-loading 1.5s infinite",
        "@keyframes skeleton-loading": {
          "0%": { backgroundPosition: "200% 0" },
          "100%": { backgroundPosition: "-200% 0" },
        },
      }}
    >
      <Box display="flex" alignItems="flex-start" gap={2} mb={2}>
        <Box
          sx={{
            width: { xs: 32, md: 55 },
            height: { xs: 32, md: 60 },
            borderRadius: "50%",
            backgroundColor: "#E0E0E0",
          }}
        />
        <Box flex={1}>
          <Box
            sx={{
              height: "20px",
              backgroundColor: "#E0E0E0",
              borderRadius: "4px",
              mb: 1,
              width: "60%",
            }}
          />
          <Box
            sx={{
              height: "16px",
              backgroundColor: "#E0E0E0",
              borderRadius: "4px",
              width: "40%",
            }}
          />
        </Box>
      </Box>
      <Box
        sx={{
          height: "60px",
          backgroundColor: "#E0E0E0",
          borderRadius: "4px",
          mb: 1,
        }}
      />
      <Box
        sx={{
          height: "40px",
          backgroundColor: "#E0E0E0",
          borderRadius: "4px",
        }}
      />
    </Box>
  );

  return (
    <Box
      sx={{
        py: { xs: 4, md: 6, lg: 4 },
        backgroundColor: "#ffffff",
        mt: "-2%",
        direction: isArabic ? "rtl" : "ltr",
      }}
    >
      <Container maxWidth="xl">
        {/* Header */}
        <Box
          textAlign={{
            xs: isArabic ? "right" : "center",
            sm: isArabic ? "right" : "left",
            md: "center",
            lg: "center",
          }}
          mb={{ xs: 4, md: 6, lg: 8 }}
        >
          <Chip
            label={t("testimonialsSection.chip")}
            sx={{
              backgroundColor: "white",
              border: "1px solid #0206174D",
              mb: 2,
              color: "#818E98",
              fontSize: { xs: "10px", md: "16px" },
              fontWeight: "500",
              textTransform: "capitalize",
              px: { xs: 0.1, md: 1 },
              py: { xs: 1.3, md: 2.2 },
              fontFamily: "var(--font-outfit)",
              borderRadius: "40px",
            }}
          />

          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontWeight: "600",
              mb: 1.4,
              color: "#1e293b",
              fontSize: {
                xs: "1.75rem",
                sm: "2rem",
                md: "2.5rem",
                lg: "2.7rem",
                xl: "3.2rem",
              },
              lineHeight: 1.2,
              fontFamily: "var(--font-plusjakarta)",
              px: { xs: 2, sm: 0 },
              textAlign: {
                xs: isArabic ? "right" : "left",
                sm: isArabic ? "right" : "left",
                md: "center",
              },
              maxWidth: "1000px",
              mx: "auto",
            }}
          >
            {t("testimonialsSection.title1")}
            <span style={{ color: "#775CE5" }}>
              {t("testimonialsSection.titleHighlight")}
            </span>
            {t("testimonialsSection.title2")}
          </Typography>

          <Typography
            sx={{
              color: "#81838c",
              fontWeight: "400",
              mb: 1.2,
              fontSize: {
                xs: "16px",
                sm: "18px",
                md: "18px",
                lg: "18px",
                xl: "24px",
              },
              fontFamily: "var(--font-outfit)",
              maxWidth: { xs: "100%", md: 900, lg: 1060 },
              textAlign: {
                xs: isArabic ? "right" : "left",
                sm: isArabic ? "right" : "left",
                md: "center",
              },
              mx: { md: "auto" },
              px: { xs: 2, sm: 0, md: 0 },
            }}
          >
            {t("testimonialsSection.subtitle")}
          </Typography>

          <Box
            display="flex"
            alignItems="center"
            justifyContent={{
              xs: isArabic ? "flex-end" : "flex-start",
              sm: isArabic ? "flex-end" : "flex-start",
              md: "center",
            }}
            gap={1}
            flexWrap="wrap"
            px={{ xs: 2, sm: 0 }}
          >
            <Image
              src={IMAGES.RATING_STARS}
              alt="5 stars"
              width={110}
              height={40}
              style={{ objectFit: "contain", marginTop: ".2%" }}
            />
            <Typography
              sx={{
                fontSize: {
                  xs: "16px",
                  sm: "18px",
                  md: "18px",
                  lg: "18px",
                  xl: "22px",
                },
                fontFamily: "var(--font-outfit)",
                color: "#81838c",
                fontWeight: "400",
              }}
            >
              {t("testimonialsSection.rating")}
            </Typography>
          </Box>
        </Box>

        {/* Testimonials Grid */}
        <Box
          sx={{
            display: {
              xs: "grid",
              sm: "grid",
              md: "grid",
              lg: "flex",
              xl: "flex",
            },
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr",
              md: "repeat(2, 1fr)",
            },
            gap: { xs: 3, md: 4 },
            justifyContent: { lg: "center" },
            flexWrap: { lg: "wrap" },
            justifyItems: { xs: "center", sm: "center", md: "center" },
          }}
        >
          {!isContentLoaded ? (
            <>
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </>
          ) : (
            <>
              {/* Column 1 - Sajid Video Card */}
              <Card
                sx={{
                  width: "100%",
                  maxWidth: { xs: "100%", sm: "100%", md: "100%", lg: 300 },
                  maxHeight: "419px",
                  borderRadius: "20px",
                  overflow: "hidden",
                  bgcolor: "#F8F3FF",
                  boxShadow: "none",
                }}
              >
                <Box sx={{ position: "relative" }}>
                  <CardMedia
                    component="video"
                    src={IMAGES.SAJID_VIDEO}
                    title="Sajid Sharif from Alecto Media using the best AI recruiting tool for talent acquisition"
                    autoPlay
                    loop
                    muted
                    playsInline
                    controlsList="nodownload noremoteplayback nofullscreen"
                    disablePictureInPicture
                    onContextMenu={(e) => e.preventDefault()}
                    sx={{
                      height: { xs: 200, sm: 180 },
                      objectFit: "cover",
                    }}
                  />
                  <IconButton
                    aria-label="Play testimonial video"
                    onClick={() => handleVideoClick(IMAGES.SAJID_VIDEO)}
                    sx={{
                      position: "absolute",
                      bottom: 8,
                      left: isArabic ? "auto" : 8,
                      right: isArabic ? 8 : "auto",
                      bgcolor: "rgba(0,0,0,0.6)",
                      color: "#fff",
                      width: { xs: 40, sm: 50 },
                      height: { xs: 40, sm: 50 },
                      "&:hover": { bgcolor: "rgba(0,0,0,0.8)" },
                    }}
                  >
                    <PlayArrowIcon />
                  </IconButton>
                </Box>

                <CardContent sx={{ p: 2 }}>
                  <Typography
                    fontWeight={500}
                    sx={{
                      fontSize: { xs: "1rem", md: "1.2rem" },
                      color: "#2A4A45",
                      fontFamily: "var(--font-outfit)",
                      ml: isArabic ? 0 : "1%",
                      mr: isArabic ? "1%" : 0,
                      textAlign: isArabic ? "right" : "left",
                    }}
                  >
                    {t("testimonialsSection.card1Name")}
                  </Typography>
                  <Typography
                    fontWeight={400}
                    sx={{
                      fontSize: { xs: "0.65rem", md: "0.7rem" },
                      color: "#6F6A6A",
                      fontFamily: "var(--font-outfit)",
                      mb: 1,
                      ml: isArabic ? 0 : "1%",
                      mr: isArabic ? "1%" : 0,
                      textAlign: isArabic ? "right" : "left",
                    }}
                  >
                    {t("testimonialsSection.card1Role")}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: { xs: "15px", md: "17px" },
                      lineHeight: 1.5,
                      color: "#2A4A45",
                      fontFamily: "var(--font-outfit)",
                      ml: isArabic ? 0 : "1%",
                      mr: isArabic ? "1%" : 0,
                      mt: "6%",
                      fontWeight: "400",
                      textAlign: isArabic ? "right" : "left",
                    }}
                  >
                    {t("testimonialsSection.card1Text")}
                  </Typography>
                </CardContent>
              </Card>

              {/* Column 2 - Hadeesa Text Card */}
              <Box
                sx={{
                  width: "100%",
                  maxHeight: "319px",
                  maxWidth: { xs: "100%", sm: "100%", md: "100%", lg: 300 },
                  mt: { xs: 0, lg: "4.5%" },
                }}
              >
                <TextCard>
                  <Box
                    display="flex"
                    alignItems="flex-start"
                    gap={2}
                    mb={2}
                    flexDirection={isArabic ? "row" : "row"}
                  >
                    <Box
                      sx={{
                        width: { xs: 32, md: 60 },
                        height: { xs: 32, md: 50 },
                        borderRadius: "50%",
                        overflow: "hidden",
                        flexShrink: 0,
                      }}
                    >
                      <Image
                        src={IMAGES.HADEESA_AVATAR}
                        alt="Hadeesa Fatima review of top recruiting software at Xtel G Solutions"
                        width={60}
                        height={60}
                        style={{
                          objectFit: "cover",
                          width: "100%",
                          height: "100%",
                        }}
                      />
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          fontSize: { xs: "1rem", md: "1.2rem" },
                          color: "#2A4A45",
                          fontFamily: "var(--font-outfit)",
                          fontWeight: 500,
                          textAlign: isArabic ? "right" : "left",
                        }}
                      >
                        {t("testimonialsSection.card2Name")}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: { xs: "0.65rem", md: "0.7rem" },
                          color: "#6F6A6A",
                          fontFamily: "var(--font-outfit)",
                          textAlign: isArabic ? "right" : "left",
                        }}
                      >
                        {t("testimonialsSection.card2Role")}
                      </Typography>
                    </Box>
                  </Box>

                  <Typography
                    sx={{
                      fontSize: { xs: "15px", md: "17px" },
                      color: "#2A4A45",
                      fontFamily: "var(--font-outfit)",
                      mt: { xs: 2, lg: "-2%" },
                      textAlign: isArabic ? "right" : "left",
                    }}
                  >
                    {t("testimonialsSection.card2Text")}
                  </Typography>
                </TextCard>
              </Box>

              {/* Column 3 - Moazzam Video Card */}
              <Box
                sx={{
                  width: "100%",
                  maxHeight: "380px",
                  maxWidth: { xs: "100%", sm: "100%", md: "100%", lg: 300 },
                }}
              >
                <VideoCard>
                  <VideoThumbnail
                    sx={{
                      height: {
                        xs: "300px",
                        sm: "350px",
                        md: "380px",
                        lg: "100%",
                      },
                    }}
                  >
                    <Box
                      component="video"
                      autoPlay
                      loop
                      muted
                      playsInline
                      preload="auto"
                      type="video/mp4"
                      title="CEO testimonial about recruitment automation software from Moazzam Kamran"
                      controlsList="nodownload noremoteplayback nofullscreen"
                      disablePictureInPicture
                      onContextMenu={(e) => e.preventDefault()}
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        position: "absolute",
                        top: 0,
                        left: 0,
                      }}
                      src={IMAGES.MOAZZAM_VIDEO}
                    />
                    <VideoDurationBadge
                      onClick={() => handleVideoClick(IMAGES.MOAZZAM_VIDEO)}
                      sx={{
                        cursor: "pointer",
                        left: isArabic ? "auto" : "12px",
                        right: isArabic ? "12px" : "auto",
                        flexDirection: isArabic ? "row" : "row",
                        width: "91.5%",
                      }}
                    >
                      <PlayArrowIcon sx={{ fontSize: "14px" }} />
                      <Box sx={{ textAlign: isArabic ? "right" : "left" }}>
                        <Box
                          sx={{
                            fontWeight: "500",
                            fontSize: { xs: "13px", md: "14px" },
                            fontFamily: "var(--font-outfit)",
                          }}
                        >
                          {t("testimonialsSection.card3Name")}
                        </Box>
                        <Box
                          sx={{
                            fontSize: { xs: "11px", md: "12px" },
                            opacity: 0.9,
                            fontFamily: "var(--font-outfit)",
                            mt: "2%",
                          }}
                        >
                          1:45
                        </Box>
                      </Box>
                    </VideoDurationBadge>
                  </VideoThumbnail>
                </VideoCard>
              </Box>

              {/* Column 4 - Iqra Text Card */}
              <Box
                sx={{
                  width: "100%",
                  maxWidth: { xs: "100%", sm: "100%", md: "100%", lg: 300 },
                  maxHeight: "330px",
                  mt: { xs: 0, lg: "3%" },
                }}
              >
                <TextCard sx={{ height: "100%" }}>
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={2}
                    flexDirection={isArabic ? "row" : "row"}
                  >
                    <Box
                      sx={{
                        width: { xs: 32, md: 60 },
                        height: { xs: 32, md: 50 },
                        borderRadius: "50%",
                        overflow: "hidden",
                        flexShrink: 0,
                      }}
                    >
                      <Image
                        src={IMAGES.IQRA_AVATAR}
                        alt="Iqra Fatima testimonial about automated recruiting software at Tezeract"
                        width={60}
                        height={60}
                        style={{
                          objectFit: "cover",
                          width: "100%",
                          height: "100%",
                        }}
                      />
                    </Box>

                    <Box>
                      <Typography
                        sx={{
                          fontSize: { xs: "1rem", md: "1.2rem" },
                          color: "#2A4A45",
                          fontFamily: "var(--font-outfit)",
                          fontWeight: 500,
                          textAlign: isArabic ? "right" : "left",
                        }}
                      >
                        {t("testimonialsSection.card4Name")}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: { xs: "0.65rem", md: "0.7rem" },
                          color: "#6F6A6A",
                          fontFamily: "var(--font-outfit)",
                          mb: { xs: 2, lg: "6%" },
                          textAlign: isArabic ? "right" : "left",
                        }}
                      >
                        {t("testimonialsSection.card4Role")}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: { xs: "15px", md: "17px" },
                      color: "#2A4A45",
                      fontFamily: "var(--font-outfit)",
                      fontWeight: "400",
                      pt: "6%",
                      textAlign: isArabic ? "right" : "left",
                    }}
                  >
                    {t("testimonialsSection.card4Text")}
                  </Typography>
                </TextCard>
              </Box>
            </>
          )}
        </Box>
      </Container>
      {/* CTA Section */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
          width: "100%",
          mx: 0,
          px: 0,
          mt: { xs: isArabic ? "7%" : "3%", sm: "3%" },
          height: { xs: isArabic ? "25vh" : "45vh", sm: "45vh" },
        }}
      >
        {/* Background Video */}
        <Box
          component="video"
          src={IMAGES.CTA_BG_IMG}
          autoPlay
          loop
          muted
          playsInline
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            zIndex: 0,
          }}
        />

        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 1,
          }}
        />

        {/* Main Heading */}
        <Typography
          sx={{
            position: "relative",
            zIndex: 2,
            fontSize: { xs: "18px", md: "28px", lg: "32px" },
            color: "#2A4A45",
            fontFamily: "var(--font-outfit)",
            fontWeight: "700",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "1px",
            flexWrap: "wrap",
          }}
        >
          {t("testimonialsSection.ctaLabel")}{" "}
          <Box
            component="img"
            src={
              isArabic
                ? IMAGES.PURPLE_QUESTION_MARK_ARABIC_IMG
                : IMAGES.PURPLE_QUESTION_MARK_IMG
            }
            alt="?"
            sx={{
              width: { xs: "22px", md: "32px", lg: "36px" },
              height: { xs: "22px", md: "32px", lg: "36px" },
              objectFit: "contain",
              display: "inline-block",
              verticalAlign: "middle",
              "@keyframes bounceRotate": {
                "0%": { transform: "translateY(0px) rotate(0deg)" },
                "20%": { transform: "translateY(-6px) rotate(-8deg)" },
                "40%": { transform: "translateY(0px) rotate(8deg)" },
                "60%": { transform: "translateY(-3px) rotate(-4deg)" },
                "80%": { transform: "translateY(0px) rotate(4deg)" },
                "100%": { transform: "translateY(0px) rotate(0deg)" },
              },
              animation: "bounceRotate 2.5s ease-in-out infinite",
            }}
          />
        </Typography>

        <Typography
          sx={{
            position: "relative",
            zIndex: 2,
            fontSize: { xs: "13px", md: "16px", lg: "18px" },
            color: "#555",
            fontFamily: "var(--font-outfit)",
            fontWeight: "400",
            mt: "10px",
          }}
        >
          {t("testimonialsSection.ctaSubtitle")}
        </Typography>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={{ xs: 1.5, sm: 2 }}
          alignItems={{ xs: "stretch", sm: "center" }}
          sx={{ position: "relative", zIndex: 2, mt: "1.5%" }}
        >
          <NextLink href="/free-trial-of-ai-recruiting-tool" passHref legacyBehavior>
            <Button
              component="a"
              sx={{
                bgcolor: "#775CE5",
                color: "#fff",
                textTransform: "none",
                px: { xs: 2.5, sm: 3, md: 3.5 },
                py: { xs: 1.5, sm: 1.6, md: 1.5 },
                height: { xs: "48px", sm: "52px", md: "55px" },
                fontFamily: "var(--font-outfit)",
                fontSize: { xs: "0.875rem", sm: "0.95rem", lg: "1rem" },
                fontWeight: "500",
                borderRadius: "28px",
                boxShadow: "none",
                transition: "all 0.3s ease-in-out",
                "&:hover": {
                  bgcolor: "#000",
                  color: "#fff",
                  transform: "scale(1.05)",
                },
              }}
            >
              {t("testimonialsSection.cta")}
            </Button>
          </NextLink>
        </Stack>
      </Box>
      {/* Video Player Modal */}
      <Modal
        open={isVideoModalOpen}
        onClose={() => {
          setIsVideoModalOpen(false);
          setIsPlaying(false);
          setProgress(0);
          setCurrentTime(0);
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backdropFilter: "blur(10px)",
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: "90vw",
            height: "80vh",
            maxWidth: "1200px",
            maxHeight: "800px",
            backgroundColor: "#000",
            borderRadius: "12px",
            overflow: "hidden",
            boxShadow: "0 25px 50px rgba(0,0,0,0.5)",
            outline: "none",
          }}
        >
          {/* Close Button */}
          <IconButton
            onClick={() => {
              setIsVideoModalOpen(false);
              setIsPlaying(false);
              setProgress(0);
              setCurrentTime(0);
              if (videoRef.current) {
                videoRef.current.currentTime = 0;
              }
            }}
            sx={{
              position: "absolute",
              top: 16,
              right: isArabic ? "auto" : 16,
              left: isArabic ? 16 : "auto",
              zIndex: 3,
              backgroundColor: "rgba(0,0,0,0.7)",
              color: "white",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.9)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>

          {/* Video Player */}
          <video
            ref={videoRef}
            autoPlay
            onEnded={handleVideoEnd}
            onTimeUpdate={handleTimeUpdate}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
            }}
          >
            <source src={selectedVideoUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>

          {/* Custom Controls */}
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              padding: "16px",
              background:
                "linear-gradient(to top, rgba(0,0,0,0.8), transparent)",
              zIndex: 3,
            }}
          >
            {/* Progress Bar */}
            <Box
              ref={progressBarRef}
              onMouseDown={handleMouseDown}
              sx={{
                width: "100%",
                height: "6px",
                backgroundColor: "rgba(255,255,255,0.3)",
                borderRadius: "3px",
                cursor: "pointer",
                mb: 2,
                position: "relative",
                "&:hover": { height: "8px" },
                "&:hover .progress-thumb": {
                  opacity: 1,
                  transform: "translate(-50%, -50%) scale(1)",
                },
              }}
            >
              <Box
                sx={{
                  width: `${progress}%`,
                  height: "100%",
                  backgroundColor: "#9048FB",
                  borderRadius: "3px",
                  transition: isDragging ? "none" : "width 0.1s linear",
                }}
              />
              <Box
                className="progress-thumb"
                sx={{
                  position: "absolute",
                  left: `calc(${progress}% - 8px)`,
                  top: "50%",
                  transform: "translateY(-50%) scale(0.8)",
                  width: "16px",
                  height: "16px",
                  backgroundColor: "#fff",
                  borderRadius: "50%",
                  cursor: "grab",
                  opacity: isDragging ? 1 : 0,
                  transition: "opacity 0.2s",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.3)",
                  "&:active": {
                    cursor: "grabbing",
                    transform: "translateY(-50%) scale(1.1)",
                  },
                }}
              />
            </Box>

            {/* Controls Row */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexDirection: isArabic ? "row-reverse" : "row",
              }}
            >
              <IconButton
                onClick={togglePlayPause}
                sx={{
                  backgroundColor: "rgba(0,0,0,0.7)",
                  color: "white",
                  width: 40,
                  height: 40,
                  "&:hover": { backgroundColor: "rgba(0,0,0,0.9)" },
                }}
              >
                {isPlaying ? (
                  <Box
                    sx={{ width: 16, height: 16, display: "flex", gap: "4px" }}
                  >
                    <Box sx={{ width: 4, height: 16, bgcolor: "white" }} />
                    <Box sx={{ width: 4, height: 16, bgcolor: "white" }} />
                  </Box>
                ) : (
                  <PlayArrowIcon />
                )}
              </IconButton>

              <Typography
                sx={{
                  color: "white",
                  fontSize: "14px",
                  fontFamily: "var(--font-outfit)",
                }}
              >
                {formatTime(currentTime)} / {formatTime(duration)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
});

TestimonialsComponent.displayName = "TestimonialsComponent";

export default TestimonialsComponent;
